// ** Redux Imports
import { createSlice } from "@reduxjs/toolkit";

import defaultNavigation from "./defaultNavigation";

// ** ThemeConfig Import
import themeConfig from "@configs/themeConfig";
import { logFilter } from "@logging";

const initialMenuCollapsed = () => {
  const item = window.localStorage.getItem("menuCollapsed");
  //** Parse stored json or if none return initialValue
  return item ? JSON.parse(item) : themeConfig.layout.menu.isCollapsed;
};

const initialDirection = () => {
  const item = window.localStorage.getItem("direction");
  //** Parse stored json or if none return initialValue
  return item ? JSON.parse(item) : themeConfig.layout.isRTL;
};

const initialSkin = () => {
  logFilter("appData", "initialSkin");
  const item = window.localStorage.getItem("skin");
  //** Parse stored json or if none return initialValue
  return item ? JSON.parse(item) : themeConfig.layout.skin;
};

export const layoutSlice = createSlice({
  name: "layout",
  initialState: {
    skin: initialSkin(),
    canImpersonate: false,
    canImpersonateExecutive: false,
    isCustomerAdmin: false,
    isDeveloper: false,
    isEmployee: false,
    isFieldEngineer: false,
    isFieldAdmin: false,
    isOperations: false,
    isExecutive: false,
    isDeviceManagement: false,
    isFloorManagement: false,
    isDemandResponseAdmin: false,
    isDemandResponseUser: false,
    isAllBuildings: false,
    isRTL: initialDirection(),
    layout: themeConfig.layout.type,
    lastLayout: themeConfig.layout.type,
    menuCollapsed: initialMenuCollapsed(),
    footerType: themeConfig.layout.footer.type,
    navbarType: themeConfig.layout.navbar.type,
    menuHidden: themeConfig.layout.menu.isHidden,
    contentWidth: themeConfig.layout.contentWidth,
    routerTransition: themeConfig.layout.routerTransition,
    navbarColor: themeConfig.layout.navbar.backgroundColor,
    navigationOptions: defaultNavigation({ isDeveloper: false }),
    locale: "en-US",
  },
  reducers: {
    handleRTL: (state, action) => {
      state.isRTL = action.payload;
      window.localStorage.setItem("direction", JSON.stringify(action.payload));
    },
    handleSkin: (state, action) => {
      logFilter("redux", "handleSkin", state, action);
      state.skin = action.payload;
      window.localStorage.setItem("skin", JSON.stringify(action.payload));
    },
    handleLayout: (state, action) => {
      state.layout = action.payload;
    },
    handleFooterType: (state, action) => {
      state.footerType = action.payload;
    },
    handleNavbarType: (state, action) => {
      state.navbarType = action.payload;
    },
    handleMenuHidden: (state, action) => {
      state.menuHidden = action.payload;
    },
    handleLastLayout: (state, action) => {
      state.lastLayout = action.payload;
    },
    handleNavbarColor: (state, action) => {
      state.navbarColor = action.payload;
    },
    handleContentWidth: (state, action) => {
      state.contentWidth = action.payload;
    },
    handleMenuCollapsed: (state, action) => {
      state.menuCollapsed = action.payload;
      window.localStorage.setItem(
        "menuCollapsed",
        JSON.stringify(action.payload),
      );
    },
    handleRouterTransition: (state, action) => {
      state.routerTransition = action.payload;
    },
    handleNavigationOptions: (state, action) => {
      // logFilter("redux", "handleNavigationOptions", state, action);
      state.navigationOptions = action.payload;
    },
    handleDidLogin: (state, action) => {
      const roles = action.payload.acl.roles;

      state.loginCount = state.loginCount + 1;
      state.canImpersonate = roles.canImpersonate;
      state.canImpersonateExecutive = roles.canImpersonateExecutive;
      state.isCustomerAdmin = roles.isCustomerAdmin;
      state.isDeveloper = roles.isDeveloper;
      state.isEmployee = roles.isEmployee;
      state.isFieldEngineer = roles.isFieldEngineer;
      state.isFieldAdmin = roles.isFieldAdmin;
      state.isOperations = roles.isOperations;
      state.isExecutive = roles.isExecutive;
      state.isDeviceManagement = roles.isDeviceManagement;
      state.isDemandResponseAdmin = roles.isDemandResponseAdmin;
      state.isDemandResponseUser = roles.isDemandResponseUser;
      state.isFloorManagement =
        roles.isDeveloper ||
        roles.isDeviceManagement ||
        roles.isOperations ||
        roles.isFieldAdmin;
      state.isServiceWellStats =
        roles.isDeveloper ||
        roles.isDeviceManagement ||
        roles.isOperations ||
        roles.isFieldAdmin;
      state.isManageIAQIndexes =
        roles.isCustomerAdmin ||
        roles.isDeveloper ||
        roles.isOperations ||
        roles.isFieldAdmin;
      state.isAllBuildings = roles.isAllBuildings;
      state.isIncludeSteamPeakAdvisor = roles.isIncludeSteamPeakAdvisor;
      state.navigationOptions = defaultNavigation({
        isManageIAQIndexes: state.isManageIAQIndexes,
        isDeveloper: state.isDeveloper,
        isFieldEngineer: state.isFieldEngineer,
        isFieldAdmin: state.isFieldAdmin,
        isDeviceManagement: state.isDeviceManagement,
        isOperations: state.isOperations,
        isAllBuildings: state.isAllBuildings,
        isDemandResponseAdmin: state.isDemandResponseAdmin,
        isDemandResponseUser: state.isDemandResponseUser,
      });
    },
  },
});

export const {
  handleRTL,
  handleSkin,
  handleLayout,
  handleLastLayout,
  handleMenuHidden,
  handleNavbarType,
  handleFooterType,
  handleNavbarColor,
  handleContentWidth,
  handleMenuCollapsed,
  handleRouterTransition,
  handleNavigationOptions,
  handleDidLogin,
} = layoutSlice.actions;

export default layoutSlice.reducer;
